/**
 * Lightweight in-app toast (RLY-024).
 *
 * Phase-1 portal MVP doesn't need the full Radix Toast surface — we ship a
 * tiny context-driven toaster that exposes a `useToast()` hook plus a
 * `<Toaster />` mount point. Toasts auto-dismiss after a configurable
 * timeout (default 4s) and are keyboard-accessible (focusable region with
 * `role="status"`).
 *
 * Replace with `@radix-ui/react-toast` if/when accessibility audits demand
 * the full primitive (queueing, swipe-to-dismiss, animation tokens).
 */
import * as React from "react";

import { cn } from "@/lib/utils";

export type ToastVariant = "default" | "success" | "error";

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
}

interface ToastContextValue {
  toasts: Toast[];
  toast: (t: Omit<Toast, "id">) => string;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 4_000;

export function ToastProvider({
  children,
  durationMs = DEFAULT_DURATION_MS,
}: {
  children: React.ReactNode;
  durationMs?: number;
}) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timersRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const toast = React.useCallback(
    (t: Omit<Toast, "id">): string => {
      const id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setToasts((prev) => [...prev, { ...t, id }]);
      const timer = setTimeout(() => dismiss(id), durationMs);
      timersRef.current.set(id, timer);
      return id;
    },
    [dismiss, durationMs],
  );

  React.useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const value = React.useMemo<ToastContextValue>(
    () => ({ toasts, toast, dismiss }),
    [toasts, toast, dismiss],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast() must be used inside a <ToastProvider> tree.");
  }
  return ctx;
}

const variantClass: Record<ToastVariant, string> = {
  default: "bg-background border-border text-foreground",
  success:
    "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-50 dark:border-emerald-800",
  error: "bg-destructive/10 border-destructive/40 text-destructive",
};

export function Toaster() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) return null;
  const { toasts, dismiss } = ctx;
  return (
    <div
      data-testid="toaster"
      aria-live="polite"
      role="status"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4 sm:right-4 sm:left-auto sm:items-end"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          data-testid={`toast-${t.variant ?? "default"}`}
          className={cn(
            "pointer-events-auto w-full max-w-sm rounded-md border p-4 shadow-lg",
            variantClass[t.variant ?? "default"],
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold">{t.title}</p>
              {t.description && <p className="mt-1 text-sm opacity-90">{t.description}</p>}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="rounded text-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
